import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { GoodThing, RichTextContent } from '../../types/good-things';
import { goodThingsService, clearAllCaches } from '../../services/goodThings';
import { PersonSelect } from './PersonSelect';
import { toast } from '../ui/use-toast';
import { ToastAction } from '../ui/toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { RichTextEditor } from './RichTextEditor';
import { convertPlainTextToRichText, extractPlainText } from '../../utils/richTextUtils';
import '../../styles/select-hover.css';
import '../../styles/category-select.css';

interface EditGoodThingDialogProps {
  goodThing: GoodThing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface FormData {
  category: string;
  person1: string;
  person2: string;
  person3: string;
  person4: string;
  person5: string;
  person6: string;
  person7: string;
  note: string;
  richNote: RichTextContent;
}

export const EditGoodThingDialog: React.FC<EditGoodThingDialogProps> = ({
  goodThing,
  open,
  onOpenChange,
  onSuccess
}) => {
  // Initialize form data with the existing Good Thing data
  const [formData, setFormData] = useState<FormData>({
    category: goodThing.category,
    person1: goodThing.recipients[0] || '',
    person2: goodThing.recipients[1] || '',
    person3: goodThing.recipients[2] || '',
    person4: goodThing.recipients[3] || '',
    person5: goodThing.recipients[4] || '',
    person6: goodThing.recipients[5] || '',
    person7: goodThing.recipients[6] || '',
    note: goodThing.note,
    richNote: goodThing.richNote || convertPlainTextToRichText(goodThing.note)
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [people, setPeople] = useState<string[]>([]);
  const originalNote = goodThing.note; // Store original note for identification

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const data = await goodThingsService.getDropdownData();
        setCategories(data.categories);
        setPeople(data.people);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load categories and people. Please try again.',
          variant: 'destructive'
        });
      }
    };

    loadDropdownData();
  }, []);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category || !formData.person1 || !formData.note.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    // Show a toast notification that the edit is in progress
    toast({
      title: "Editing Good Thing...",
      description: "Your Good Thing is being updated. Please wait.",
      variant: "default"
    });

    setIsSubmitting(true);

    try {
      // Filter out empty recipients
      const recipients = [
        formData.person1,
        formData.person2,
        formData.person3,
        formData.person4,
        formData.person5,
        formData.person6,
        formData.person7
      ].filter(person => person !== '');

      // Create updated Good Thing object
      const updatedGoodThing: GoodThing = {
        ...goodThing,
        category: formData.category,
        recipients,
        note: formData.note,
        richNote: formData.richNote
      };

      // Close dialog immediately to show we're processing
      onOpenChange(false);

      // Update the Good Thing
      await goodThingsService.updateGoodThing(updatedGoodThing, originalNote);
      
      // Clear caches to ensure fresh data
      clearAllCaches();

      // Add a delay to allow time for the backend to process the update
      // and for Firebase to sync the changes
      setTimeout(() => {
        // Show success toast without refresh button since sync works automatically
        toast({
          title: "Good Thing Updated! ✅",
          description: "Your Good Thing has been successfully updated.",
          variant: "success",
          duration: 5000, // 5 seconds
        });
      }, 5000); // 5 second delay

      // Trigger success callback
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error updating Good Thing:', error);
      toast({
        title: 'Error',
        description: 'Failed to update the Good Thing. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="dark:text-white">Edit Good Thing</DialogTitle>
          <DialogDescription className="dark:text-gray-400">
            Update the details of this good thing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Required Fields */}
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-1 space-y-4">
              {/* Date field (disabled) */}
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-gray-200">Date</label>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-md border dark:border-gray-600 text-sm dark:text-gray-300">
                  {new Date(goodThing.date).toLocaleDateString()}
                </div>
              </div>
              
              {/* Submitter field (disabled) */}
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-gray-200">From</label>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-md border dark:border-gray-600 text-sm dark:text-gray-300">
                  {goodThing.submitter}
                </div>
              </div>
              
              {/* Primary Recipient */}
              <PersonSelect
                value={formData.person1}
                onValueChange={(value) => handleChange('person1', value)}
                people={people}
                label="To"
              />
              
              {/* Category field */}
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-gray-200">Category</label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleChange('category', value)}
                  required
                >
                  <SelectTrigger className="category-select-trigger dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="select-content-with-hover">
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="col-span-2 space-y-4">
              {/* Note field */}
              <div className="space-y-2">
                <label className="text-sm font-medium dark:text-gray-200">Note</label>
                <RichTextEditor
                  value={formData.richNote}
                  onChange={(richNote) => {
                    setFormData(prev => ({
                      ...prev,
                      richNote,
                      note: extractPlainText(richNote)
                    }));
                  }}
                  placeholder="Enter the good thing note..."
                  className="min-h-[200px]"
                />
              </div>
            </div>
          </div>

          {/* Optional People Section */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="additional-recipients">
              <AccordionTrigger className="text-sm dark:text-gray-200">Additional Recipients (Optional)</AccordionTrigger>
              <AccordionContent className="px-2">
                <div className="grid grid-cols-3 gap-6 pt-4">
                  {[2, 3, 4, 5, 6, 7].map((num) => (
                    <PersonSelect
                      key={num}
                      value={formData[`person${num}` as keyof FormData] as string}
                      onValueChange={(value) => handleChange(`person${num}` as keyof FormData, value)}
                      people={people}
                      label={`Person ${num}`}
                      isOptional={true}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {isSubmitting ? 'Updating...' : 'Update Good Thing'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
